import React, { useEffect, useState } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import { Button, ButtonGroup, Container, Col, Row } from 'react-bootstrap'
import moment from 'moment'
import { saveAs } from 'file-saver'

import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl } from 'Shared/utils'
import TimeSeriesPlot from 'Shared/Components/TimeSeriesPlot'
import { FloatingDatePicker, FloatingTimeRangePicker, FloatingInputDisabled } from 'Shared/Components/FloatingLabel/FloatingLabel'
import { gasDetecorMetadata } from 'Shared/constant'
import BackLinkButton from 'Shared/Components/BackLinkButton'

const GasDataHistory = () => {
  const { t } = useTranslation()
  const { device_id } = useParams()
  const { send } = useFetchData()
  const { projectInfo, setPageLoading } = useOutletContext()

  const [data, setData] = useState()
  const [deviceName, setDeviceName] = useState()

  const [threshold, setThreshold] = useState({})
  const [referenceLines, setReferenceLines] = useState({})

  const [date, setDate] = useState(new Date())
  const [time, setTime] = useState([new Date(moment(date).format("YYYY-MM-DD 00:00:00")), new Date(moment(date).format("YYYY-MM-DD 23:59:59"))])
  const [plotRange, setPlotRange] = useState({ "start": 0, "end": 24 })

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        loadData(),
        loadDeivceName()
      ])

      setPageLoading(false)
    }
    
    init()
  }, [])

  const loadDeivceName = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/gas_detector/devices`,
      params: {
        "device_id": device_id
      },
      returnType: "json"
    })

    body && setDeviceName(body[0]["device_name"])
  }

  const loadData = async () => {
    const body1 = await send({
      method: "GET",
      url: `${apiUrl}/gas_detector/threshold`,
      returnType: "json"
    })

    const body2 = await send({
      method: "GET",
      url: `${apiUrl}/gas_detector/history`,
      params: {
        "device_id": device_id,
        "start_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[0]).format("HH:mm:ss")}`).unix(),
        "end_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[1]).format("HH:mm:ss")}`).unix()
      },
      returnType: "json"
    })

    const th = {}
    const refLines = {}

    if (body2 != null) {
      Object.entries(body1).forEach(([k, v]) => {
        if (projectInfo["info"]["gas"]["gas_types"].includes(k)) {
          const max = Math.max(...(body2.map(obj => obj[k])))
          const min = Math.min(...(body2.map(obj => obj[k])))

          refLines[k] = [...new Set(v.flatMap(item => item.range).filter(value => value !== null && value !== undefined))]

          v.forEach((i, index) => {
            if (i["alert"] > 0) {
              th[`${k}-${i["alert"]}-${index}`] = [i["range"][0] ?? Math.min(gasDetecorMetadata[k]["plotRange"][0], min), i["range"][1] ?? Math.max(gasDetecorMetadata[k]["plotRange"][1], max)]
            }
          })
        }
      })

      setThreshold(th)
      setReferenceLines(refLines)

      setData([
        ...[{...th, ...{"time": moment.unix(0)}}],
        ...body2.map((i) => ({...th, ...{
          "h2s": i["h2s"],
          "co": i["co"],
          "co2": i["co2"],
          "o2": i["o2"],
          "ch4": i["ch4"],
          "pm25": i["pm25"],
          "temperature": i["temperature"],
          "combustible_gas": i["combustible_gas"],
          "battery_level": i["battery_level"],
          "time": new Date(moment(i["updated_at"]))
        }})),
        ...[{...th, ...{"time": moment.unix(2147483648)}}],
      ])
    }
    else {
      setData()
    }
  }

  const update = async () => {
    if (date && time) {
      setPageLoading(true)

      await loadData()

      const start = parseInt(moment(time[0]).startOf('hour').format('HH'))

      let end = moment(time[1])
      if (end.format("mm") !== "00") { end = end.add(1, "hours").startOf('hours') }
      end = end.format('HH')

      setPlotRange({
        "start": start,
        "end": end !== "00" ? parseInt(end) : 24
      })

      setPageLoading(false)
    }
  }

  const report = async () => {
    setPageLoading(true)

    const blob = await send({
      method: "GET",
      url: `${apiUrl}/gas_detector/report`,
      params: {
        "device_id": device_id,
        "start_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[0]).format("HH:mm:ss")}`).unix(),
        "end_time": moment(`${moment(date).format("YYYY-MM-DD")} ${moment(time[1]).format("HH:mm:ss")}`).unix()
      },
      returnType: "blob"
    })

    if (blob != null) {
      saveAs(blob, `export.xlsx`)
    }

    setPageLoading(false)
  }

  return (<>
    <div className="mainContainer"  style={{ borderRadius: "15px", backgroundColor: "white", padding: "20px" }}>
      <div style={{ width: "100%", height: "100%", overflow: "auto" }}>
        <BackLinkButton />
        
        <Container fluid style={{ padding: "0px 15px 0px 15px" }}>
          <Row className="mt-1 mb-1">
            <Col xs={12} lg={4}>
              <FloatingInputDisabled label="Device Name" name="device" value={deviceName} />
            </Col>

            <Col xs={12} lg={4}>
              <FloatingDatePicker label="Date" name="date" value={date} onChange={(e) => setDate(new Date(e))}/>
            </Col>

            <Col xs={12} lg={4}>
              <FloatingTimeRangePicker label="Time Range" name="time" value={time}  onChange={(d) => setTime([new Date(d[0]), new Date(d[1])]) }/>
            </Col>

            <Col xs={12} className="mt-2">
              <ButtonGroup style={{ width: "100%" }}>
                <Button variant="success" onClick={() => update()}>{t("general_search")}</Button>
                <Button variant="info" onClick={() => report()}>{t("general_export")}</Button>
              </ButtonGroup>
            </Col>
          </Row>

          {data && 
            <Row style={{ marginTop: "20px" }}>
              <Col xs={12} style={{ height: "100%", padding: "10px" }}>
                <Stack spacing={3}>
                  {projectInfo["info"]["gas"]["gas_types"].map((i) =>
                    <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                      <div style={{ width: "100%", textAlign: "center" }}>
                        <b>{t(gasDetecorMetadata[i]["label"])}</b>
                      </div>

                      <div style={{ flexGrow: 1, margin: "5px" }}>
                        <TimeSeriesPlot
                          data={data ?? []}
                          threshold_key={Object.keys(threshold).filter((j) => j.split("-")[0] === i)}
                          items={[
                            { "axis": "left", "dataKey": i, "name": t(gasDetecorMetadata[i]["label"]), "stroke": "#800080" },
                          ]}
                          day={moment(date).format("YYYY-MM-DD")}
                          hour={[plotRange["start"], plotRange["end"]]}
                          yLeft={{ "range": gasDetecorMetadata[i]["plotRange"], "label": "" }}
                          referenceLines={referenceLines[i]}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ height: "400px", border: "1px solid black", borderRadius: "8px", display: "flex", flexDirection: "column", padding: "10px" }}>
                    <div style={{ width: "100%", textAlign: "center" }}>
                      <b>{t("gas_batteryLevel")}</b>
                    </div>

                    <div style={{ flexGrow: 1, margin: "5px" }}>
                      <TimeSeriesPlot
                        data={data ?? []}
                        threshold_key={[]}
                        items={[
                          { "axis": "left", "dataKey": "battery_level", "name": t("floodingSensor_batteryLevel"), "stroke": "#800080" },
                        ]}
                        day={moment(date).format("YYYY-MM-DD")}
                        hour={[plotRange["start"], plotRange["end"]]}
                        yLeft={{ "range": [0, 100], "label": "" }}
                      />
                    </div>
                  </div>
                </Stack>
              </Col>
            </Row>
          }
        </Container>
      </div>
    </div>
  </>)
}

export default GasDataHistory
